import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import { paginateItems } from './assignmentPagination';


export const initialMeaningSelections = (items = []) => Object.fromEntries(
  items.map((item) => [
    item.entry_id,
    Array.isArray(item.recommended_sense_ids)
      ? [...new Set(item.recommended_sense_ids.filter(Boolean))]
      : (item.recommended_sense_id ? [item.recommended_sense_id] : []),
  ])
);

const visibleMeaningIds = (item = {}) => {
  const available = new Set(
    (item.senses || []).map((sense) => sense.sense_id).filter(Boolean),
  );
  (item.proposed_senses || []).forEach((sense) => {
    if (sense?.choice_id) available.add(sense.choice_id);
  });
  if ((!item.proposed_senses || item.proposed_senses.length === 0) && item.kind === 'new_word') {
    available.add('new-word');
  }
  if (
    (!item.proposed_senses || item.proposed_senses.length === 0)
    && item.kind === 'existing_word'
    && item.meaning_hint
    && item.proposed_sense_is_distinct === true
  ) {
    available.add('new');
  }
  return available;
};

export const validMeaningSelections = (items = [], selections = {}) => items.map((item) => {
  const available = visibleMeaningIds(item);
  const selected = Array.isArray(selections[item.entry_id])
    ? selections[item.entry_id]
    : [selections[item.entry_id]].filter(Boolean);
  return {
    entry_id: item.entry_id,
    sense_ids: [...new Set(selected)].filter((senseId) => available.has(senseId)),
  };
});

export const countValidMeaningSelections = (items = [], selections = {}) => (
  validMeaningSelections(items, selections)
    .reduce((total, item) => total + item.sense_ids.length, 0)
);

export const toggleMeaningSelection = (selections, entryId, senseId, checked) => {
  const current = Array.isArray(selections?.[entryId])
    ? selections[entryId]
    : (selections?.[entryId] ? [selections[entryId]] : []);
  const next = checked
    ? [...new Set([...current, senseId])]
    : current.filter((value) => value !== senseId);
  return { ...selections, [entryId]: next };
};


const SenseChoice = ({ item, sense, selected, onSelect }) => (
  <label className={`flex cursor-pointer gap-3 border-t px-3 py-3 first:border-t-0 ${selected ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}>
    <input
      type="checkbox"
      value={sense.sense_id}
      checked={selected}
      onChange={(event) => onSelect(item.entry_id, sense.sense_id, event.target.checked)}
      className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
    />
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-2">
        <strong data-i18n-skip className="text-sm text-gray-900">{sense.definition_cn || sense.definition_en}</strong>
        {sense.pos && <span data-i18n-skip className="text-xs text-gray-500">{sense.pos}</span>}
        {sense.is_primary && <span className="text-xs font-medium text-blue-700">Primary</span>}
        {sense.status === 'provisional' && <span className="text-xs font-medium text-amber-700">Teacher-added</span>}
      </span>
      {sense.definition_en && <span data-i18n-skip className="mt-1 block text-xs leading-5 text-gray-600">{sense.definition_en}</span>}
    </span>
  </label>
);


const AssignmentMeaningReview = ({
  items = [],
  selections = {},
  onSelectionChange,
  onRetry,
  resetKey,
}) => {
  const { t } = useI18n();
  const [page, setPage] = useState(1);
  const pagination = useMemo(() => paginateItems(items, page, 12), [items, page]);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const typoCount = items.filter((item) => item.kind === 'typo').length;
  const failedCount = items.filter(
    (item) => item.inspection_status === 'failed'
      || item.inspection_status === 'retrying'
      || item.kind === 'inspection_error'
  ).length;
  const generationCount = items.reduce((total, item) => {
    const selected = Array.isArray(selections[item.entry_id])
      ? selections[item.entry_id]
      : [selections[item.entry_id]].filter(Boolean);
    return total + selected.filter(
      (value) => value === 'new'
        || value === 'new-word'
        || value.startsWith('new:')
        || value.startsWith('new-word:')
    ).length;
  }, 0);

  return (
    <section className="border border-gray-300 bg-white" aria-label="Review vocabulary meanings">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-gray-50 px-4 py-3">
        <div>
          <h4 className="font-semibold text-gray-900">Check words and meanings</h4>
          <p className="mt-0.5 text-xs text-gray-600">
            Choose the meanings to teach. Leave every box unchecked to skip a word.
          </p>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="text-gray-600">{items.length} entries</span>
          {generationCount > 0 && <span className="font-medium text-amber-700">{generationCount} need new practice activities</span>}
          {typoCount > 0 && <span className="font-medium text-red-700">{typoCount} need correction</span>}
          {failedCount > 0 && <span className="font-medium text-red-700">{failedCount} need retry</span>}
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between border-b bg-white px-4 py-2 text-xs text-gray-600">
          <span>{t('Page {page} of {pages}', { page: pagination.page, pages: pagination.pages })}</span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={pagination.page <= 1} className="rounded border border-gray-300 p-1.5 disabled:cursor-not-allowed disabled:opacity-40" title={t('Previous page')} aria-label={t('Previous page')}>
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setPage((current) => Math.min(pagination.pages, current + 1))} disabled={pagination.page >= pagination.pages} className="rounded border border-gray-300 p-1.5 disabled:cursor-not-allowed disabled:opacity-40" title={t('Next page')} aria-label={t('Next page')}>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-gray-300">
        {pagination.items.map((item) => {
          const selected = Array.isArray(selections[item.entry_id])
            ? selections[item.entry_id]
            : [selections[item.entry_id]].filter(Boolean);
          if (
            item.inspection_status === 'failed'
            || item.inspection_status === 'retrying'
            || item.kind === 'inspection_error'
          ) {
            const retrying = item.inspection_status === 'retrying';
            return (
              <div key={item.entry_id} className="flex flex-wrap items-start gap-3 bg-red-50 px-4 py-4">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  <div data-i18n-skip className="font-semibold text-red-950">{item.word || 'Unresolved entry'}</div>
                  {item.source_text && (
                    <p data-i18n-skip className="mt-1 break-words text-xs text-red-900">
                      Original: {item.source_text}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-red-800">
                    {item.inspection_message || 'This entry could not be checked.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onRetry?.(item)}
                  disabled={retrying || !onRetry}
                  className="inline-flex min-h-10 items-center gap-2 border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:cursor-wait disabled:opacity-60"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
                  {retrying ? 'Checking...' : 'Try again'}
                </button>
              </div>
            );
          }
          if (item.kind === 'typo') {
            const retrying = item.inspection_status === 'retrying';
            return (
              <div key={item.entry_id} className="flex flex-wrap gap-3 bg-red-50 px-4 py-4">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  <div data-i18n-skip className="font-semibold text-red-900">{item.word}</div>
                  <p className="mt-1 text-sm text-red-800">This looks like a possible typo, so it will be skipped unless you retry it.</p>
                  {item.suggested_correction && (
                    <p className="mt-1 text-sm text-red-800">Suggested correction: <strong data-i18n-skip>{item.suggested_correction}</strong></p>
                  )}
                </div>
                {item.suggested_correction && onRetry && (
                  <button
                    type="button"
                    onClick={() => onRetry?.({ ...item, retry_source_text: item.suggested_correction })}
                    disabled={retrying || !onRetry}
                    className="inline-flex min-h-10 items-center gap-2 border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:cursor-wait disabled:opacity-60"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
                    {retrying ? 'Checking...' : 'Use correction'}
                  </button>
                )}
              </div>
            );
          }

          return (
            <div key={item.entry_id} className="grid gap-3 px-4 py-4 lg:grid-cols-[180px_minmax(0,1fr)]">
              <div>
                <div className="flex items-center gap-2">
                  {item.kind === 'new_word'
                    ? <PlusCircleIcon className="h-5 w-5 text-emerald-600" />
                    : <CheckCircleIcon className="h-5 w-5 text-blue-600" />}
                  <strong data-i18n-skip className="text-gray-950">{item.word}</strong>
                  {selected.length === 0 && (
                    <span className="border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      Skipped
                    </span>
                  )}
                </div>
                {item.meaning_hint && <p data-i18n-skip className="mt-1 text-xs text-gray-600">Teacher input: {item.meaning_hint}</p>}
                {Array.isArray(item.source_texts) && item.source_texts.length > 1 && (
                  <p className="mt-1 text-xs font-medium text-blue-700">
                    Combined {item.source_texts.length} entries
                  </p>
                )}
                <p className="mt-2 text-xs font-medium text-gray-500">
                  {item.kind === 'new_word' ? 'New word' : `${item.senses?.length || 0} available meanings`}
                </p>
              </div>

              <div className="border border-gray-200">
                <div className="border-b bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  Select any meanings, or leave all unchecked to skip this word.
                </div>
                {(item.senses || []).map((sense) => (
                  <SenseChoice
                    key={sense.sense_id}
                    item={item}
                    sense={sense}
                    selected={selected.includes(sense.sense_id)}
                    onSelect={onSelectionChange}
                  />
                ))}

                {(item.proposed_senses || []).map((sense) => (
                  <SenseChoice
                    key={sense.choice_id}
                    item={item}
                    sense={{
                      ...sense,
                      sense_id: sense.choice_id,
                      definition_cn: sense.definition_cn || 'Create this new meaning',
                      definition_en: sense.definition_en,
                      status: 'provisional',
                    }}
                    selected={selected.includes(sense.choice_id)}
                    onSelect={onSelectionChange}
                  />
                ))}

                {(!item.proposed_senses || item.proposed_senses.length === 0) && item.kind === 'new_word' && (
                  <SenseChoice
                    item={item}
                    sense={{
                      sense_id: 'new-word',
                      ...item.proposed_sense,
                      status: 'provisional',
                    }}
                    selected={selected.includes('new-word')}
                    onSelect={onSelectionChange}
                  />
                )}

                {(!item.proposed_senses || item.proposed_senses.length === 0)
                  && item.kind === 'existing_word'
                  && item.meaning_hint
                  && item.proposed_sense_is_distinct === true
                  && (
                    <SenseChoice
                      item={item}
                      sense={{
                        sense_id: 'new',
                        ...item.proposed_sense,
                        definition_cn: item.proposed_sense?.definition_cn || 'Create this new meaning',
                        status: 'provisional',
                      }}
                      selected={selected.includes('new')}
                      onSelect={onSelectionChange}
                    />
                  )}
              </div>
            </div>
          );
        })}
      </div>
      {pagination.pages > 1 && (
        <div className="flex justify-end border-t bg-white px-4 py-2 text-xs text-gray-600">
          <span>{t('Page {page} of {pages}', { page: pagination.page, pages: pagination.pages })}</span>
        </div>
      )}
    </section>
  );
};

export default AssignmentMeaningReview;
